import * as v from 'valibot';

import { Permission } from '../auth/auth';
import { ResultsAndCount } from './common.types';

export const ACTIVE = 'Active';
export const INACTIVE = 'Inactive';
export const UserStatus = v.picklist([ACTIVE, INACTIVE], 'user status');
export type UserStatus = v.InferOutput<typeof UserStatus>;

export const CLAIMS = 1;
export const UNDERWRITING = 2;
export const BOTH = 3;
export const UserDepartment = v.picklist(
	[CLAIMS, UNDERWRITING, BOTH],
	'user department',
);
export type UserDepartment = v.InferOutput<typeof UserDepartment>;

export const departmentLabels: Record<UserDepartment, string> = {
	[CLAIMS]: 'Claims',
	[UNDERWRITING]: 'Underwriting',
	[BOTH]: 'Both',
};

// Helper to convert department to number for API (already a number, just for consistency)
export const departmentToEnum = (dept: UserDepartment): number => {
	return dept;
};

// Helper to convert number to UserDepartment
export const enumToDepartment = (enumValue: number): UserDepartment | null => {
	if (enumValue === CLAIMS) return CLAIMS;
	if (enumValue === UNDERWRITING) return UNDERWRITING;
	if (enumValue === BOTH) return BOTH;
	return null;
};

// Organisation assignment schema
export const OrganisationAssignment = v.object({
	organisationId: v.number(),
	stateIds: v.array(v.string()),
	regionIds: v.array(v.string()),
	officeIds: v.array(v.string()),
	countryIds: v.array(v.string()),
	assignedAllCountries: v.boolean(),
	assignedAllRegions: v.boolean(),
	assignedAllStates: v.boolean(),
	assignedAllOffices: v.boolean(),
	viewOtherUsersRisks: v.boolean(),
	viewOtherUsersClaims: v.boolean(),
});

export type OrganisationAssignment = v.InferOutput<
	typeof OrganisationAssignment
>;

export const UserReference = v.object({
	id: v.string(),
	name: v.string(),
});

export const UserDirectReports = v.object({
	...UserReference.entries,
	directReports: v.nullish(v.array(UserReference)),
});

export type UserReference = v.InferOutput<typeof UserReference>;

export const User = v.object({
	userId: v.string(),
	firstName: v.string(),
	lastName: v.string(),
	emailAddress: v.string(),
	roleIds: v.array(v.string()),
	organisationNames: v.array(v.string()),
	organisationIds: v.array(v.number()),
	roleNames: v.array(v.string()),
	stateIds: v.optional(v.array(v.string())),
	stateNames: v.array(v.string()),
	regionIds: v.optional(v.array(v.string())),
	regionNames: v.array(v.string()),
	countryIds: v.optional(v.array(v.string())),
	countryNames: v.array(v.string()),
	officeIds: v.optional(v.array(v.string())),
	officeNames: v.array(v.string()),
	jobTitleName: v.nullish(v.string()),
	status: UserStatus,
	phoneNumber: v.nullish(v.string()),
	lastLogin: v.nullish(v.string()),
	assignedAllCompanies: v.boolean(),
	assignedAllCountries: v.optional(v.boolean()),
	assignedAllRegions: v.optional(v.boolean()),
	assignedAllStates: v.optional(v.boolean()),
	assignedAllOffices: v.optional(v.boolean()),
});

export type User = v.InferOutput<typeof User>;

export const UserId = v.object({
	userId: v.string(),
});

export type UserId = v.InferOutput<typeof UserId>;

export const SingleUser = v.object({
	id: v.string(),
	firstName: v.string(),
	lastName: v.string(),
	phoneNumber: v.nullish(v.string()),
	emailAddress: v.string(),
	roleIds: v.array(v.string()),
	organisations: v.array(OrganisationAssignment),
	lastLogin: v.nullish(v.string()),
	organisationNames: v.array(v.string()),
	roleNames: v.array(v.string()),
	permissions: v.optional(
		v.array(
			v.pipe(
				v.string(),
				v.transform((i) => i as Permission),
			),
		),
	),
	stateIds: v.optional(v.array(v.string())),
	regionIds: v.optional(v.array(v.string())),
	officeIds: v.optional(v.array(v.string())),
	countryIds: v.optional(v.array(v.string())),
	status: v.nullish(UserStatus),
	jobTitleName: v.nullish(v.string()),
	assignedAllCompanies: v.boolean(),
	assignedAllCountries: v.optional(v.boolean()),
	assignedAllRegions: v.optional(v.boolean()),
	assignedAllStates: v.optional(v.boolean()),
	assignedAllOffices: v.optional(v.boolean()),
	reportsTo: v.nullish(UserReference),
	directReports: v.nullish(v.array(UserDirectReports)),
	underwriterReportsTo: v.nullish(UserReference),
	directUnderwriterReports: v.nullish(v.array(UserDirectReports)),
	departments: v.optional(v.array(v.number())),
});

export type SingleUser = v.InferOutput<typeof SingleUser>;

export const UsersQuery = v.object({
	skip: v.number(),
	take: v.number(),
	searchText: v.optional(v.string()),
	emailAddress: v.optional(v.string()),
	sortBy: v.optional(v.string()),
	sortDirection: v.optional(v.picklist(['asc', 'desc'])),
	role: v.optional(v.array(v.string())),
	countries: v.optional(v.array(v.string())),
	organisations: v.optional(v.array(v.number())),
	regions: v.optional(v.array(v.string())),
	states: v.optional(v.array(v.string())),
	offices: v.optional(v.array(v.string())),
	jobTitle: v.optional(v.array(v.string())),
	status: v.optional(UserStatus),
});

export type UsersQuery = v.InferOutput<typeof UsersQuery>;

export type UsersResults = ResultsAndCount<User>;

export type UserBody = {
	firstName: string;
	lastName: string;
	emailAddress: string;
	phoneNumber: string | null;
	roleIds: string[];
	organisations: OrganisationAssignment[];
	jobTitle: string | null;
	assignedAllCompanies: boolean;
	reportsToUserId?: string | null;
	directReportUserIds?: string[] | null;
	underwriterReportsToUserId?: string | null;
	directUnderwriterReportUserIds?: string[] | null;
	departments: number[];
};

export const UserSummary = v.object({
	id: v.string(),
	fullName: v.string(),
});

export type UserSummary = v.InferOutput<typeof UserSummary>;
